import * as yup from "yup";

import {
  Alert,
  BoxProps,
  Button,
  Divider,
  Flex,
  Image,
  Text,
} from "@chakra-ui/react";
import { AppForm, FormField, Link } from "@picovico/ui";
import {
  catchErrorHandler,
  parseApiError,
  storeLocalStorageData,
} from "@picovico/utils";
import { useLoginUserMutation } from "store/services/auth";

import { getPageRoutes } from "@/routes/pageRoutes";
import NextLink from "next/link";
import { useRouter } from "next/router";
import { useAppDispatch } from "store";
import { resetUserStateAction } from "store/slices";
import { useAppToast } from "@/hooks/useAppToast";
import { useSocialSignIn } from "@/hooks/useSocialSignIn";
import { useVisibleSocialProviders } from "@/hooks/useWebFeature";

export interface loginValues {
  email: string;
  password: string;
}

const loginFormValidationSchema = yup.object().shape({
  email: yup.string().email().required().label("Email"),
  password: yup.string().required().label("Password"),
});

const initialValues: loginValues = {
  email: "",
  password: "",
};

type SocialAuthProvider = { providerName: string; providerSvg: string };

interface LoginFormProps extends BoxProps {
  socialAuthProviders?: SocialAuthProvider[];
}

function LoginForm2({
  socialAuthProviders: configuredSocialProviders,
  ...props
}: LoginFormProps) {
  const [login, { isLoading, error }] = useLoginUserMutation({});
  const dispatch = useAppDispatch();
  const { error: errorToast } = useAppToast();
  const router = useRouter();
  const startSocialSignIn = useSocialSignIn();
  // Google is hidden while the company has google_signin off.
  const socialAuthProviders = useVisibleSocialProviders(
    configuredSocialProviders,
  );

  return (
    <AppForm<loginValues>
      formProps={{
        display: "flex",
        flexDirection: "column",
        alignItems: "start",
        justifyContent: "center",
        mt: { base: "0rem", md: "1.5rem" },
        ...props,
      }}
      apiError={(error && parseApiError(error)) || null}
      initialValues={initialValues}
      validationSchema={loginFormValidationSchema}
      onSubmit={(values) => {
        login(values)
          .unwrap()
          .then((res) => {
            if (res?.data.token) {
              storeLocalStorageData("token", res.data.token);
              storeLocalStorageData("device_id", res.data.device_id);
            }

            dispatch(resetUserStateAction());

            const nextUrl = router.query.next as string;

            if (nextUrl) {
              const target = decodeURIComponent(nextUrl);
              if (target.startsWith("/")) {
                router.push(target);
              } else {
                router.push(getPageRoutes(target));
              }
            } else {
              router.push(getPageRoutes("home"));
            }
          })
          .catch((err) => {
            catchErrorHandler(err, errorToast);
          });
      }}
    >
      <FormField
        name="email"
        type="text"
        placeholder="Enter your email address"
        rounded={"50px"}
        padding={"25px"}
        backgroundColor="#FFFFFF"
        border={"1px solid #E2F4FF"}
      />
      <FormField
        name="password"
        type="password"
        border={"1px solid #E2F4FF"}
        rounded={"50px"}
        placeholder={"Enter your password"}
        backgroundColor="#FFFFFF"
        padding={"25px"}
      />
      {error && error.message && (
        <Alert status="error" color="error.900">
          <Text fontWeight={500} color="danger.700">
            {error.message}
          </Text>
        </Alert>
      )}

      {socialAuthProviders && socialAuthProviders.length > 0 && (
        <Flex direction={"column"} justifyContent="center" alignSelf="center">
          <Text textAlign={"center"} p={4}>
            or sign in with
          </Text>
          <Flex
            direction="row"
            justifyContent="center"
            alignSelf="center"
            gap="2rem"
          >
            {socialAuthProviders.map((provider) => (
              <Button
                key={provider.providerName}
                variant="ghost"
                p="0.5rem"
                minW="auto"
                onClick={() => {
                  const verifyPath = `/social/${provider.providerName}/verify`;
                  const origin = window?.location?.origin || "";
                  const nextParam = (router.query.next as string) || "";
                  const next = nextParam
                    ? `${verifyPath}?next=${encodeURIComponent(nextParam)}`
                    : verifyPath;
                  startSocialSignIn(provider.providerName, `${origin}${next}`);
                }}
              >
                <Image
                  src={provider.providerSvg}
                  alt={provider.providerName}
                  boxSize="24px"
                />
              </Button>
            ))}
          </Flex>
        </Flex>
      )}

      <Button
        isLoading={isLoading || false}
        isDisabled={isLoading || false}
        mt="1rem"
        ml="auto"
        mr="auto"
        size="lg"
        w="200px"
        type="submit"
      >
        Sign In
      </Button>
      <Link
        as={NextLink}
        href="/forgot-password"
        mr={"auto"}
        ml={"auto"}
        mt="1rem"
        _hover={{
          color: "primary.700",
        }}
        color="primary.500"
      >
        Forgot Password?
      </Link>

      <Flex
        display={{
          base: "none",
          md: "flex",
        }}
        w="full"
        flexDirection="column"
      >
        <Divider my="1rem" />
        <Text mx="auto" fontWeight={500}>
          Don&apos;t have an account?{" "}
          <Link
            href="/register"
            color="primary.500"
            _hover={{
              color: "primary.700",
            }}
          >
            Register
          </Link>
        </Text>
      </Flex>
    </AppForm>
  );
}

export default LoginForm2;
